import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
//import { apiGet } from 'api/clientApi';
import { addProject as addProjectApi, updateProject as updateProjectApi, deleteProject as deleteProjectApi, listProjects } from 'api/clientApi';
import useAuth from 'hooks/useAuth';
import { usePipelineAutoRefresh } from 'hooks/usePipelineAutoRefresh';
import { Project, ProjectPayload } from 'types/project';
import { ProjectType } from 'types/project-type';
import { useLookups } from 'contexts/LookupsContext';



export interface ProjectContextValue {
  projects: Project[];
  projectTypes: ProjectType[];
  activeProjectId: string | null;
  activeProject: Project | null;
  pinnedProjectIds: string[];
  pinnedProjects: Project[];
  isLoading: boolean;
  error: string | null;
  isProjectsPageActive: boolean;
  setIsProjectsPageActive: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCrudUiOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refreshProjects: () => Promise<void>;
  selectProject: (projectId: string | null, opts?: { navigate?: boolean }) => void;
  /**
   * The ViewPoint (run) chosen for the active project, or null for "the latest". Master wherever a
   * page or link needs a run: the dashboard, the Cognitives pages and the left panel all follow it,
   * and only fall back to the newest run when nothing has been chosen.
   */
  activeRunId: string | null;
  /** Pass null — or the project's newest run — to follow the latest again. */
  selectRun: (runId: string | null) => void;
  pinProject: (projectId: string) => void;
  unpinProject: (projectId: string) => void;
  createProject: (payload: ProjectPayload) => Promise<Project>;
  updateProject: (id: string, payload: ProjectPayload) => Promise<Project>;
  deleteProject: (id: string) => Promise<void>;
};

/*  const isTerminalPipelineState = (status: String) => {
  //console.log(status);
  //return status === 'complete' || status === 'failed' || status === 'deleted' || status === 'cancelled';
  return status !== 'processing' && status !== 'initialising' && status !== 'cancelling';
};*/

export const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

const LS_ACTIVE = 'activeProjectId';
const LS_ACTIVE_RUN = 'activeViewpoint';

/** `{ projectId, runId }` — stored with its project so a reload cannot apply one project's run to another. */
function readStoredRun(projectId: string | null): string | null {
  if (!projectId) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(LS_ACTIVE_RUN) ?? 'null');
    return stored?.projectId === projectId && typeof stored.runId === 'string' ? stored.runId : null;
  } catch {
    return null;
  }
}

function writeStoredRun(projectId: string | null, runId: string | null) {
  try {
    if (projectId && runId) localStorage.setItem(LS_ACTIVE_RUN, JSON.stringify({ projectId, runId }));
    else localStorage.removeItem(LS_ACTIVE_RUN);
  } catch {
    // Not remembered across reloads; the selection still holds for this session.
  }
}
const LS_PINNED = 'pinnedProjectIds';



export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, appUser } = useAuth();
  const { projectTypes, findLookupByName } = useLookups();
  //console.log(projectTypes);

  const [projects, setProjects] = useState<Project[]>([]);
  //const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeProjectId, setActiveProjectId] = useState<string | null>(() => localStorage.getItem(LS_ACTIVE));
  const [selectedRunId, setSelectedRunId] = useState<string | null>(() => readStoredRun(localStorage.getItem(LS_ACTIVE)));
  const [isCrudUiOpen, setIsCrudUiOpen] = React.useState(false);
  const [isProjectsPageActive, setIsProjectsPageActive] = React.useState(false);

  const [pinnedProjectIds, setPinnedProjectIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(LS_PINNED) || '[]');
    } catch {
      return [];
    }
  });

  /*const refreshProjects = async () => {
    setIsLoading(true);
    setError(null);

    try {
      //const types = (await getProjectTypes()) as ProjectType[];
      //const types = (await getLookup("project-types")) as ProjectType[];
      //const types = await getLookup("project-types");
      //const types = getLookupByName<ProjectType>("PTYPE");

      //console.log('[SANITY] ProjectTypes:', types);
      //setProjectTypes(types);

      const data = (await listProjects()) as Project[]; //"07d53e99-80ed-4a64-b171-ef6ac608048c"

      setProjects(data);

      console.log('[SANITY] Projects:', data);



      // If active project no longer exists, clear it WITHOUT navigating.
      setActiveProjectId((currentActive) => {
        if (currentActive && !data.some((p) => p.id === currentActive)) {
          localStorage.removeItem(LS_ACTIVE);
          return null;
        }
        return currentActive;
      });

    } catch (e: any) {
      setError(e?.message || 'Failed to load projects');
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };*/

  const refreshProjects = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      try {
        const data = (await listProjects()) as Project[]; //"07d53e99-80ed-4a64-b171-ef6ac608048c"
        setProjects(data);
        console.log(data);
        setActiveProjectId((currentActive) => {
          if (currentActive && !data.some((p) => p.id === currentActive)) {
            localStorage.removeItem(LS_ACTIVE);
            return null;
          }
          return currentActive;
        });

        //setProjects(projects);
        setError(null);
      } catch (err: any) {
        setError(err.message);
      } finally {
        if (!silent) {
          setIsLoading(false);
        } else {
          setIsRefreshing(false);
        }
      }
    },
    []
  );

  usePipelineAutoRefresh({
    items: projects,
    disabled: isCrudUiOpen,
    isPageActive: isProjectsPageActive,
    onRefresh: () => refreshProjects({ silent: true }),
    getPipelineState: (project) => project.pipelineState
  });
  
  /*
    const { hasInFlightItems: hasInFlightProjects } = usePipelineAutoRefresh({
      items: projects,
      disabled: isCrudUiOpen,
      onRefresh: () => refreshProjects({ silent: true })
    });*/
  /*const hasInFlightDocuments = React.useMemo(() => {
    return projects.some((prj) => {
      //console.log(doc.pipelineState);
      const pipelineState = prj.pipelineState;
      //console.log("aaa");
      if (!pipelineState) return false;
      //console.log("bbb");
      const latestTimestamp = pipelineState.latest_timestamp as number ?? -1;
      //console.log("ccc");
      if (latestTimestamp < 0) return false;
      //console.log("ddd");
      //console.log(latestTimestamp === 0, pipelineState.runs !== undefined);
      if (latestTimestamp === 0 && pipelineState.runs) {
        //console.log("T");
        return true;
      }
      const pRun = (pipelineState.runs as Record<string, unknown>)[String(latestTimestamp)] as any;
      //console.log(pRun);
      if (!pRun?.state) return false;
      //console.log(pRun);      console.log(pRun.status);
      return !isTerminalPipelineState(pRun.state);
    });
  }, [projects]);

    React.useEffect(() => {
      if (isCrudUiOpen) return;
      const intervalMs = hasInFlightDocuments ? FAST_REFRESH_MS : NORMAL_REFRESH_MS;
      const interval = window.setInterval(() => {
        void loadDocuments();
      }, intervalMs);
  
      return () => {
        window.clearInterval(interval);
      };
    }, [isCrudUiOpen, hasInFlightDocuments]);*/

  const selectProject = (projectId: string | null) => {
    // A run belongs to one project, so changing project drops the selection back to "latest".
    if (projectId !== activeProjectId) {
      setSelectedRunId(null);
      writeStoredRun(null, null);
    }
    setActiveProjectId(projectId);
    if (projectId) localStorage.setItem(LS_ACTIVE, projectId);
    else localStorage.removeItem(LS_ACTIVE);
  };

  const deleteProject = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      await deleteProjectApi(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      setActiveProjectId((current) => (current === id ? null : current));
      await refreshProjects();
    } finally {
      setIsLoading(false);
    }
  };

  const updateProject = async (id: string, payload: ProjectPayload): Promise<Project> => {
    setIsLoading(true);
    try {
      const updated = await updateProjectApi(id, payload);

      if (!updated) {
        throw new Error('Failed to update project');
      }

      setProjects((prev) =>
        prev.map((p) => (p.id === id ? updated : p))
      );
      await refreshProjects();
      return updated;
    } finally {
      setIsLoading(false);
    }
  };

  const createProject = async (payload: ProjectPayload): Promise<Project> => {
    setIsLoading(true);
    try {
      console.log(payload);
      //const body = { name: payload.name, description: payload.description, type: payload.type, documents: payload.documents };
      const result = await addProjectApi(payload);
      if (!result) {
        throw new Error('Failed to create project');
      }
      setProjects((prev) => [result, ...prev]);
      setActiveProjectId(result.id);
      await refreshProjects();
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const pinProject = (projectId: string) => {
    setPinnedProjectIds((prev) => {
      const next = Array.from(new Set([projectId, ...prev]));
      localStorage.setItem(LS_PINNED, JSON.stringify(next));
      return next;
    });
  };

  const unpinProject = (projectId: string) => {
    setPinnedProjectIds((prev) => {
      const next = prev.filter((id) => id !== projectId);
      localStorage.setItem(LS_PINNED, JSON.stringify(next));
      return next;
    });
  };

  // load projects on login, clear on logout
  /*
  useEffect(() => {
    if (!isLoggedIn || !orgId) return;
    if (isLoggedIn) {
      refreshProjects();
    } else {
      setProjects([]);
      setError(null);
      setIsLoading(false);
      // optional: clear active project on logout
      setActiveProjectId(null);
      localStorage.removeItem(LS_ACTIVE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, orgId]);
  */


  useEffect(() => {
    if (!isLoggedIn) {
      setProjects([]);
      setError(null);
      setIsLoading(false);
      setActiveProjectId(null);
      localStorage.removeItem(LS_ACTIVE);
      return;
    }

    //if (orgId) {
    refreshProjects();
    //console.log("XXXXXX");
    //}
  }, [isLoggedIn/*, orgId*/]);


  const activeProject = useMemo(
    () => projects.find((p) => p.id === activeProjectId) || null,
    [projects, activeProjectId]
  );

  // Choosing the newest run is the same as choosing "latest", and is stored that way — otherwise
  // picking it would pin the project to that run and hide the next one when it lands.
  const selectRun = (runId: string | null) => {
    const runs = Object.keys((activeProject?.pipelineState?.runs as Record<string, unknown> | undefined) ?? {});
    const newest = runs.sort((a, b) => Number(a) - Number(b)).at(-1) ?? null;
    const next = runId && runId !== newest ? runId : null;
    setSelectedRunId(next);
    writeStoredRun(activeProjectId, next);
  };

  // A stored run the project no longer has (deleted, or another project's) reads as "latest".
  const activeRunId = useMemo(() => {
    if (!selectedRunId || !activeProject) return null;
    const runs = (activeProject.pipelineState?.runs as Record<string, unknown> | undefined) ?? {};
    return selectedRunId in runs ? selectedRunId : null;
  }, [selectedRunId, activeProject]);

  const pinnedProjects = useMemo(
    () => projects.filter((p) => pinnedProjectIds.includes(p.id)),
    [projects, pinnedProjectIds]
  );

  const value: ProjectContextValue = {
    projects,
    projectTypes,
    activeProjectId,
    activeProject,
    pinnedProjectIds,
    pinnedProjects,
    isLoading,
    error,
    isProjectsPageActive,
    setIsProjectsPageActive,
    setIsCrudUiOpen,
    refreshProjects,
    selectProject,
    activeRunId,
    selectRun,
    pinProject,
    unpinProject,
    createProject,
    updateProject,
    deleteProject
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProjects() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProjects must be used within ProjectProvider');
  return ctx;
}
